/**
 * Reconexão após update/restart da extensão.
 *
 * O Chrome pode invalidar o content script antigo sem injetar o novo numa aba já aberta. A
 * própria página precisa recarregar para receber o script atualizado. O formulário já vive no
 * localStorage (`captureFormMemory`); aqui preservamos somente qual overlay estava aberto.
 */
import { LIST_MODULES, type ListModule } from '../config/constants';
import type { CapturePhase } from './captureEngine';

const RELOAD_ATTEMPT_KEY = 'extension_reconnect_reload_attempted_v1';
const CAPTURE_WINDOW_KEY = 'extension_reconnect_capture_window_v1';

export interface ExtensionReloadState {
  active: boolean;
  phase: CapturePhase | null;
  captureWindow: ListModule | null;
}

type ReloadPage = () => void;

function captureIsRunning(state: ExtensionReloadState): boolean {
  return state.active || state.phase === 'running';
}

function rememberCaptureWindow(module: ListModule | null): void {
  if (module) sessionStorage.setItem(CAPTURE_WINDOW_KEY, module);
  else sessionStorage.removeItem(CAPTURE_WINDOW_KEY);
}

function defaultReload(): void {
  window.location.reload();
}

/** Tenta no máximo uma recarga automática por ciclo de perda/reconexão. */
export function attemptAutomaticExtensionReload(
  state: ExtensionReloadState,
  reload: ReloadPage = defaultReload,
): boolean {
  if (captureIsRunning(state)) return false;
  try {
    if (sessionStorage.getItem(RELOAD_ATTEMPT_KEY) === '1') return false;
    sessionStorage.setItem(RELOAD_ATTEMPT_KEY, '1');
    rememberCaptureWindow(state.captureWindow);
  } catch {
    // Sem storage não há como impedir loop nem restaurar o overlay: exigir ação manual.
    try {
      sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
    } catch {
      /* storage indisponível */
    }
    return false;
  }
  reload();
  return true;
}

/** Recarga solicitada pelo usuário no modal; continua proibida durante captura ativa. */
export function reloadPanelForExtensionReconnect(
  state: ExtensionReloadState,
  reload: ReloadPage = defaultReload,
): boolean {
  if (captureIsRunning(state)) return false;
  try {
    sessionStorage.setItem(RELOAD_ATTEMPT_KEY, '1');
    rememberCaptureWindow(state.captureWindow);
  } catch {
    // A ação é explícita: mesmo sem sessionStorage, recarregar é melhor que deixar o botão
    // inerte. A rota permanece e o formulário continua protegido pelo localStorage.
  }
  reload();
  return true;
}

/**
 * Limpa a trava quando o novo content script respondeu e devolve o overlay que deve ser reaberto.
 */
export function completeExtensionReconnect(): ListModule | null {
  try {
    sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
    const pendingModule = sessionStorage.getItem(CAPTURE_WINDOW_KEY);
    sessionStorage.removeItem(CAPTURE_WINDOW_KEY);
    return pendingModule && (LIST_MODULES as readonly string[]).includes(pendingModule)
      ? (pendingModule as ListModule)
      : null;
  } catch {
    return null;
  }
}
